import { useState, useCallback, useRef, useEffect } from 'react'
import { CalendarIcon } from 'lucide-react'
import { format, endOfDay } from 'date-fns'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { TimeInput } from '@/components/ui/time-input'
import {
  REPLAY_TIME_PRESETS,
  formatClockText,
  replayTimeLabel,
  type ReplayTimeSelection,
} from '@/features/charts/replay-time'
import {
  getExchangeToday,
  getExchangeNow,
  getEffectiveTradingDay as getEffectiveTradingDayET,
} from '@/lib/exchange-time'
import {
  buildAllMonths,
  parseDateStr,
  parseUserDate,
  findMonthIndex,
  isDateDisabled,
} from './chart-date-picker-helpers'

const MONTH_HEIGHT_ESTIMATE = 200

/** Width that makes the month label, the date input, and the day grid share one
 *  left edge. See the comment at the column itself for the arithmetic. */
const CALENDAR_COL_WIDTH = 248

/**
 * The custom-time dropdowns sit INSIDE the calendar popover (zIndex 60). Radix
 * portals SelectContent to <body> with the shared `z-50`, so without this the
 * menu opens *behind* the calendar that spawned it and the control looks dead.
 *
 * The height cap keeps the hour list to roughly eight rows. Radix defaults to
 * the full available viewport height, which ran the 24-entry list to the bottom
 * of the screen. Forwarded to TimeInput's `contentStyle`.
 */
const SELECT_ABOVE_POPOVER: React.CSSProperties = { zIndex: 70, maxHeight: 224 }

// ─── Component ───

/**
 * Replay wiring. Present only for chart panels, since the same calendar serves the
 * scanner and watchlist headers, which have nothing to replay.
 *
 * Spec: docs/superpowers/specs/2026-08-27-chart-replay-calendar-design.md
 */
export interface ChartDatePickerReplay {
  /** Replay mode is engaged: armed for a pick, or a session is already running. */
  active: boolean
  /** Toggling OFF while a session runs exits the replay (host's job). */
  onActiveChange: (active: boolean) => void
  /** Apply pressed: start a replay on this day at this time. */
  onStart: (dateStr: string, selection: ReplayTimeSelection) => void
  /** Start-time selection, hoisted so the host can persist it app-wide. */
  time: ReplayTimeSelection
  onTimeChange: (selection: ReplayTimeSelection) => void
  /** False on daily+ timeframes, where a time of day cannot mean anything. */
  timesEnabled: boolean
  /** Day the host wants pre-staged (⋯ menu / right-click entry points). */
  stagedDate?: string | null
}

/**
 * Scanner minute snapshot wiring (scanner header, flag on): a New York time of
 * day beside the past date, so the scan reads as of that minute. Mutually
 * exclusive with `replay`: the scanner has nothing to replay.
 */
export interface ChartDatePickerScanTime {
  /** HH:MM New York time, or null for the whole-day scan. */
  time: string | null
  /** Always a well-formed HH:MM, or null when cleared. */
  onTimeChange: (time: string | null) => void
}

interface ChartDatePickerProps {
  /** Currently selected date (YYYY-MM-DD), or null to reflect the live trading day. */
  focusDate: string | null
  /** Always passes a YYYY-MM-DD string. Even "Today" writes today explicitly,
   * so an active workspace broadcast can't shadow the picker label. */
  onFocusDate: (date: string | null) => void
  /** Opt-in replay integration (chart panels only). */
  replay?: ChartDatePickerReplay
  /** Opt-in minute-of-day field (scanner panels behind the minute-snapshot flag). */
  scanTime?: ChartDatePickerScanTime
  /** Controlled open, so entry points outside the header can raise the picker. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function ChartDatePicker({
  focusDate,
  onFocusDate,
  replay,
  scanTime,
  open: controlledOpen,
  onOpenChange,
}: ChartDatePickerProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = useCallback(
    (next: boolean) => {
      setUncontrolledOpen(next)
      onOpenChange?.(next)
    },
    [onOpenChange],
  )
  const replayActive = replay?.active ?? false

  // Recompute every render: memoizing would freeze the "today" boundary at
  // page-load and break across ET midnight rollover in long-lived tabs.
  const exchangeToday = getExchangeToday()
  const todayDate = parseDateStr(exchangeToday)!
  const effectiveTradingDay = getEffectiveTradingDayET()

  // Rebuilt on popover open so the list extends across month rollovers.
  const [allMonths, setAllMonths] = useState<Date[]>(() => buildAllMonths())

  const [dateText, setDateText] = useState('')
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const pendingScrollRef = useRef<number | null>(null)

  const isDisabledDay = useCallback((date: Date) => isDateDisabled(date, todayDate), [todayDate])

  const isLive = !focusDate || focusDate === exchangeToday
  const effectiveDate = parseDateStr(effectiveTradingDay)
  // A minute only exists on a completed session, so the field is live only for
  // a day before today (the backend rejects it otherwise).
  const pastDay = !!focusDate && focusDate < exchangeToday
  const scanTimeShown = scanTime && pastDay ? scanTime.time : null
  const displayLabel = `${focusDate ?? effectiveTradingDay}${scanTimeShown ? ` · ${scanTimeShown}` : ''}`

  const virtualizer = useVirtualizer({
    count: allMonths.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => MONTH_HEIGHT_ESTIMATE,
    overscan: 3,
    measureElement: (el) => el.getBoundingClientRect().height,
  })

  const handleOpenChange = useCallback((nextOpen: boolean) => setOpen(nextOpen), [setOpen])

  // Sync draft + rebuild months on open (covers month rollover in long-lived
  // tabs). Keyed off `open` rather than the change handler because the picker
  // is also opened programmatically (replay entry points), which never routes
  // through onOpenChange.
  const prepRef = useRef({ focusDate, effectiveDate })
  prepRef.current = { focusDate, effectiveDate }
  useEffect(() => {
    if (!open) return
    const fresh = buildAllMonths()
    setAllMonths(fresh)
    const d = prepRef.current.focusDate
      ? parseDateStr(prepRef.current.focusDate)
      : prepRef.current.effectiveDate
    setSelectedDate(d)
    setDateText(d ? format(d, 'M/d/yyyy') : '')
    pendingScrollRef.current = findMonthIndex(fresh, d ?? getExchangeNow())
  }, [open])

  const [scrollMountKey, setScrollMountKey] = useState(0)

  const scrollCallbackRef = useCallback((node: HTMLDivElement | null) => {
    scrollRef.current = node
    if (node) {
      setScrollMountKey((k) => k + 1)
    }
  }, [])

  useEffect(() => {
    if (scrollRef.current && pendingScrollRef.current != null) {
      const idx = pendingScrollRef.current
      pendingScrollRef.current = null
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          virtualizer.scrollToIndex(idx, { align: 'start' })
        })
      })
    }
  }, [scrollMountKey, open, virtualizer])

  // ─── Replay staging ───
  // Hot apply (replay off): a day click IS the answer, so commit and close.
  // Replay on: the day is only half the answer, so it stages and waits for
  // Apply alongside the start time.
  const [stagedDate, setStagedDate] = useState<string | null>(null)
  const replayStagedDate = replay?.stagedDate ?? null

  // A host that opens the picker on a specific day (right-click "Replay from
  // this day") wins over whatever was staged before.
  useEffect(() => {
    if (replayStagedDate) setStagedDate(replayStagedDate)
  }, [replayStagedDate])

  // Arming with nothing staged lands on the day the panel is already showing,
  // so Apply is usable on the first click.
  useEffect(() => {
    if (!open || !replayActive) return
    setStagedDate((cur) => cur ?? replayStagedDate ?? focusDate ?? effectiveTradingDay)
  }, [open, replayActive, replayStagedDate, focusDate, effectiveTradingDay])

  useEffect(() => {
    if (!open) setStagedDate(null)
  }, [open])

  // A past day on a scanner is only half the answer: the time field sits in
  // the same popover, so the calendar stays open for it. Today ends the pick.
  const finishPick = useCallback(
    (day: Date, dateStr: string) => {
      if (scanTime && dateStr < exchangeToday) {
        setSelectedDate(day)
        setDateText(format(day, 'M/d/yyyy'))
        return
      }
      setOpen(false)
    },
    [scanTime, exchangeToday, setOpen],
  )

  // Write the picked date explicitly (including today). Writing `null` would
  // let an active workspace broadcast shadow the click via the wrapper's fallback.
  const handleDayClick = useCallback(
    (day: Date) => {
      const dateStr = format(day, 'yyyy-MM-dd')
      if (replayActive) {
        setStagedDate(dateStr)
        return
      }
      onFocusDate(dateStr)
      finishPick(day, dateStr)
    },
    [onFocusDate, replayActive, finishPick],
  )

  const handleGoToToday = useCallback(() => {
    if (replayActive) {
      setStagedDate(exchangeToday)
      return
    }
    onFocusDate(exchangeToday)
    setOpen(false)
  }, [onFocusDate, exchangeToday, replayActive, setOpen])

  const handleDateCommit = useCallback(() => {
    const d = parseUserDate(dateText)
    if (d && d <= endOfDay(todayDate)) {
      setSelectedDate(d)
      if (replayActive) {
        setStagedDate(format(d, 'yyyy-MM-dd'))
        return
      }
      const dateStr = format(d, 'yyyy-MM-dd')
      onFocusDate(dateStr)
      finishPick(d, dateStr)
    } else {
      const current = focusDate ? parseDateStr(focusDate) : undefined
      setDateText(current ? format(current, 'M/d/yyyy') : '')
    }
  }, [dateText, focusDate, todayDate, onFocusDate, replayActive, setOpen, finishPick])

  const handleReplayApply = useCallback(() => {
    if (!replay || !stagedDate) return
    replay.onStart(stagedDate, replay.time)
    setOpen(false)
  }, [replay, stagedDate, setOpen])

  const stagedDateObj = stagedDate ? parseDateStr(stagedDate) : undefined
  const calendarSelected = replayActive ? stagedDateObj : selectedDate
  const handleDateKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleDateCommit()
    },
    [handleDateCommit],
  )

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        <button
          data-companion-label="date-picker"
          // Live mode is icon-only: fixed 30px cell to match the other header
          // segments. History mode shows the date label, so it stays fluid.
          // h-7 matches the other header segments (PillButtons are 28px), so the
          // hover background fills the same height instead of a short clipped pill.
          className={`flex h-7 items-center gap-1 rounded-sm text-[11px] text-muted-foreground transition-colors hover:bg-hover hover:text-foreground ${
            isLive ? 'w-[30px] justify-center px-0' : 'px-1.5'
          }`}
          onMouseDown={(e) => e.stopPropagation()}
          onDragStart={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          draggable={true}
        >
          {/* Date text only in history mode; on the latest date show just the
              calendar icon to keep the top bar compact. */}
          {!isLive && <span className="text-yellow-500">{displayLabel}</span>}
          <CalendarIcon
            // Live = quiet (muted like the neighboring icons). Status color is
            // reserved for the exceptional state: yellow while in history mode.
            // Green-on-market-day was decoration on the default state.
            className={`h-3.5 w-3.5 shrink-0 ${!isLive ? 'text-yellow-500' : 'text-muted-foreground'}`}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        sideOffset={6}
        onMouseDown={(e) => e.stopPropagation()}
        onOpenAutoFocus={(e) => e.preventDefault()}
        style={{ zIndex: 60 }}
      >
        <div className="flex flex-col">
          <div className="flex">
            {/* 248 = the day grid's own 232px (7 x 32px cells + react-day-picker's
                4px root padding) at an 8px left inset, leaving 16px on the right
                for the scrollbar. Every row in this column lands on that same
                8px edge: the input, the month label, and the grid's first day. */}
            <div className="flex flex-col" style={{ width: CALENDAR_COL_WIDTH }}>
              {/* Date text input + Go to Today */}
              <div className="flex items-center gap-2 px-2 pt-3 pb-2">
                <Input
                  value={dateText}
                  onChange={(e) => setDateText(e.target.value)}
                  onBlur={handleDateCommit}
                  onKeyDown={handleDateKeyDown}
                  placeholder="Type a date (e.g. 3/15/2024)"
                  className="h-7 text-xs flex-1"
                  style={{ borderColor: 'hsl(var(--border-strong))' }}
                />
                <button
                  onClick={handleGoToToday}
                  className="shrink-0 rounded px-2 py-1 text-[10px] font-medium text-primary hover:bg-accent transition-colors"
                >
                  Today
                </button>
              </div>

              {/* Virtualized scrollable calendar */}
              <div
                ref={scrollCallbackRef}
                className="border-t overflow-y-auto"
                style={{ height: 300, borderColor: 'hsl(var(--border-strong))' }}
              >
                <div
                  style={{
                    height: virtualizer.getTotalSize(),
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {virtualizer.getVirtualItems().map((virtualRow) => {
                    const month = allMonths[virtualRow.index]
                    return (
                      <div
                        key={virtualRow.index}
                        ref={virtualizer.measureElement}
                        data-index={virtualRow.index}
                        className="px-1"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        {/* px-1 here + px-1 on the label = the 8px edge the day
                            grid lands on via the wrapper's 4px plus the picker's
                            own 4px root padding. */}
                        <div className="px-1 pt-3 pb-0 text-xs font-semibold text-foreground">
                          {format(month, 'MMMM yyyy').toUpperCase()}
                        </div>
                        <Calendar
                          mode="single"
                          month={month}
                          numberOfMonths={1}
                          hideNavigation
                          selected={calendarSelected}
                          onSelect={() => {
                            /* handled by onDayClick */
                          }}
                          onDayClick={handleDayClick}
                          disabled={isDisabledDay}
                          classNames={{ month_caption: 'hidden' }}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Scanner minute snapshot: same side-panel shape as the replay
                column below, so the two read as one family. */}
            {scanTime && (
              <div
                data-testid="scan-time-panel"
                className="flex flex-col border-l"
                style={{ width: 196, borderColor: 'hsl(var(--border-strong))' }}
              >
                <div className="flex items-center px-3 pt-3 pb-2">
                  <span className="flex h-7 items-center text-[11px] font-semibold text-foreground">
                    Time (ET)
                  </span>
                </div>
                <div
                  className="flex flex-col gap-1 border-t px-3 pt-2 pb-3"
                  style={{ borderColor: 'hsl(var(--border-strong))' }}
                >
                  {!pastDay && (
                    <div
                      data-testid="scan-time-disabled-note"
                      className="mb-1 rounded border border-amber-500/60 bg-amber-500/10 px-2 py-1.5 text-[11px] font-medium leading-snug text-amber-600 dark:text-amber-400"
                    >
                      Pick a past day to scan as of a minute.
                    </div>
                  )}
                  <TimeInput
                    value={scanTime.time}
                    onChange={(next) => scanTime.onTimeChange(next || null)}
                    disabled={!pastDay}
                    clearable
                    ariaLabel="Scan time"
                    contentStyle={SELECT_ABOVE_POPOVER}
                  />
                </div>
              </div>
            )}

            {/* Replay start-time column: the "side panel" half of the two-step
            form. Only mounted while replay mode is engaged, so the calendar
            stays exactly as narrow as before for ordinary date picking. */}
            {replayActive && replay && (
              <div
                data-testid="replay-time-panel"
                className="flex flex-col border-l"
                style={{ width: 196, borderColor: 'hsl(var(--border-strong))' }}
              >
                {/* Header band mirrors the date row opposite it: same pt-3/pb-2
                    around a 28px control, so "Start at" sits on the same
                    baseline as "Today" and the rule below runs straight across
                    both columns. */}
                <div className="flex items-center px-3 pt-3 pb-2">
                  <span className="flex h-7 items-center text-[11px] font-semibold text-foreground">
                    Start at
                  </span>
                </div>
                <div
                  className="flex flex-col gap-1 border-t px-3 pt-2 pb-3"
                  style={{ borderColor: 'hsl(var(--border-strong))' }}
                >
                  {/* Daily+ disables everything below, so the reason goes FIRST
                      and wears a border. As a quiet grey footnote it read as
                      decoration, and the disabled controls read as broken. */}
                  {!replay.timesEnabled && (
                    <div
                      data-testid="replay-times-disabled-note"
                      className="mb-1 rounded border border-amber-500/60 bg-amber-500/10 px-2 py-1.5 text-[11px] font-medium leading-snug text-amber-600 dark:text-amber-400"
                    >
                      Select an intraday timeframe to use the start time.
                    </div>
                  )}
                  {REPLAY_TIME_PRESETS.map((preset) => {
                    const selected = replay.time.preset === preset.id
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        data-testid={`replay-time-${preset.id}`}
                        aria-pressed={selected}
                        disabled={!replay.timesEnabled}
                        onClick={() => replay.onTimeChange({ ...replay.time, preset: preset.id })}
                        // Selected reads as SELECTED (primary fill + border) and
                        // everything else stays at full foreground weight. The
                        // muted/faded scheme this replaced was unreadable against
                        // the popover.
                        className={`flex items-baseline justify-between gap-2 rounded border px-2 py-1.5 text-left text-xs transition-colors disabled:opacity-40 ${
                          selected
                            ? 'border-primary bg-primary/20 font-semibold text-foreground'
                            : 'border-transparent text-foreground hover:bg-hover'
                        }`}
                      >
                        <span>{preset.label}</span>
                        <span className="tabular-nums text-muted-foreground">
                          {formatClockText(preset.hour, preset.minute)}
                        </span>
                      </button>
                    )
                  })}
                  <button
                    type="button"
                    data-testid="replay-time-custom"
                    aria-pressed={replay.time.preset === 'custom'}
                    disabled={!replay.timesEnabled}
                    onClick={() => replay.onTimeChange({ ...replay.time, preset: 'custom' })}
                    className={`flex items-baseline justify-between gap-2 rounded border px-2 py-1.5 text-left text-xs transition-colors disabled:opacity-40 ${
                      replay.time.preset === 'custom'
                        ? 'border-primary bg-primary/20 font-semibold text-foreground'
                        : 'border-transparent text-foreground hover:bg-hover'
                    }`}
                  >
                    <span>Custom</span>
                    {replay.time.preset === 'custom' && (
                      <span className="tabular-nums text-muted-foreground">
                        {replayTimeLabel(replay.time)}
                      </span>
                    )}
                  </button>
                  {replay.time.preset === 'custom' && (
                    // The shared TimeInput, so the replay clock reads and edits
                    // the same 24-hour way as every filter and node parameter.
                    <div className="pt-0.5">
                      <TimeInput
                        value={replay.time.custom}
                        onChange={(next) => replay.onTimeChange({ ...replay.time, custom: next })}
                        disabled={!replay.timesEnabled}
                        ariaLabel="Custom replay"
                        contentStyle={SELECT_ABOVE_POPOVER}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Replay footer: the toggle that turns hot apply into a two-step
            form, plus the Apply that commits day + time. Fixed height: the
            Apply button appearing must not resize the popover under the
            cursor. */}
          {replay && (
            <div
              data-testid="replay-footer"
              className="flex shrink-0 items-center justify-between gap-2 border-t px-3"
              style={{ height: 44, borderColor: 'hsl(var(--border-strong))' }}
            >
              <label className="flex cursor-pointer items-center gap-2 text-[11px] font-medium text-foreground">
                <Switch
                  data-testid="replay-mode-toggle"
                  aria-label="Replay mode"
                  checked={replayActive}
                  onCheckedChange={replay.onActiveChange}
                  // The default unchecked track is `bg-input`, which all but
                  // disappears on the popover surface. An off toggle the user
                  // cannot find reads as a missing feature.
                  className="data-[state=unchecked]:bg-muted-foreground/50"
                />
                <span>Replay mode</span>
              </label>
              {replayActive && (
                <Button
                  data-testid="replay-apply"
                  size="sm"
                  className="h-7 px-3 text-[11px]"
                  disabled={!stagedDate}
                  onClick={handleReplayApply}
                >
                  Apply
                </Button>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
